import { useEffect, useState } from "react";
import type {
  AgentWorkbenchPatch,
  AgentWorkbenchSectionContent,
  AgentWorkSurfaceManifest,
  AgentWorkSurfaceSection,
} from "../../../../shared/types";
import { artifactWorkspaceViewModel } from "../../lib/artifact-workspace";
import { WorkbenchComposer } from "./WorkbenchComposer";
import { buildPreviewDiffs } from "./WorkbenchPatchPreview";
import { Button } from "../ui/Button";

type ArtifactDocumentReviewProps = {
  manifest: AgentWorkSurfaceManifest;
  proposal?: AgentWorkbenchPatch | null;
  proposalError?: string | null;
  proposalPending?: boolean;
  applyPending?: boolean;
  provenanceLabel?: string;
  onRequestProposal?: (request: {
    target_section_id?: string | null;
    quick_action_id?: string | null;
    instruction?: string | null;
  }) => Promise<boolean> | boolean;
  onApplyProposal?: () => void;
  onAskAgainProposal?: () => void;
  onRejectProposal?: () => void;
  onCancelProposal?: () => void;
};

export function ArtifactDocumentReview({
  manifest,
  proposal = null,
  proposalError = null,
  proposalPending = false,
  applyPending = false,
  provenanceLabel = "Brand artifact",
  onRequestProposal,
  onApplyProposal,
  onAskAgainProposal,
  onRejectProposal,
  onCancelProposal,
}: ArtifactDocumentReviewProps) {
  const workspace = artifactWorkspaceViewModel(manifest, { provenanceLabel, proposal });
  const patchableSections = manifest.sections.filter((section) => section.patchable);
  const diffs = proposal ? buildPreviewDiffs(manifest, proposal) : [];
  const changedSectionIds = new Set(diffs.map((diff) => diff.sectionId));
  const initialSectionId = proposal?.target_section_id ?? patchableSections[0]?.id ?? null;
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(initialSectionId);
  const [manualEditMode, setManualEditMode] = useState(false);

  useEffect(() => {
    const validIds = new Set(patchableSections.map((section) => section.id));
    if (!selectedSectionId || !validIds.has(selectedSectionId)) {
      setSelectedSectionId(initialSectionId);
    }
  }, [initialSectionId, patchableSections, selectedSectionId]);

  const selectedSection =
    manifest.sections.find((section) => section.id === selectedSectionId) ??
    patchableSections[0] ??
    manifest.sections[0] ??
    null;
  const selectedDiff = diffs.find((diff) => diff.sectionId === selectedSection?.id) ?? diffs[0] ?? null;
  const isEditing = manualEditMode || Boolean(proposal);

  useEffect(() => {
    if (proposal) {
      setManualEditMode(false);
    }
  }, [proposal]);

  return (
    <section className="overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4">
        <div>
          <p className="font-data text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
            Readable artifact document
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-slate-950">{manifest.title}</h2>
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium text-slate-600">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
              {workspace.provenanceLabel}
            </span>
            {selectedSection ? (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                Section {selectedSection.id}
              </span>
            ) : null}
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
              Version {workspace.manifestVersion}
            </span>
            {workspace.updatedAtLabel ? (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                Updated {workspace.updatedAtLabel}
              </span>
            ) : null}
            {workspace.revisionState === "revision_pending" ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-800">
                Revision pending
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {proposal ? (
            <>
              <Button variant="secondary" onClick={onRejectProposal} disabled={applyPending || proposalPending}>
                Reject
              </Button>
              <Button variant="primary" onClick={onApplyProposal} disabled={applyPending || proposalPending}>
                {applyPending ? "Applying..." : "Apply markdown revision"}
              </Button>
            </>
          ) : patchableSections.length > 0 ? (
            <Button
              variant={isEditing ? "secondary" : "primary"}
              onClick={() => setManualEditMode((editing) => !editing)}
            >
              {isEditing ? "Done editing" : "Enter edit mode"}
            </Button>
          ) : null}
        </div>
      </div>

      <article className="space-y-5 px-5 py-5 md:px-7 md:py-6">
          {manifest.sections.map((section) => (
            <ArtifactDocumentSection
              key={section.id}
              section={section}
              changed={changedSectionIds.has(section.id)}
              selected={isEditing && selectedSection?.id === section.id}
              diff={diffs.find((diff) => diff.sectionId === section.id) ?? null}
              rationale={proposal?.rationale ?? ""}
              editingEnabled={isEditing}
              onSelect={isEditing && section.patchable ? setSelectedSectionId : undefined}
            />
          ))}
        {selectedDiff ? (
          <RevisionPanel
            manifest={manifest}
            sectionLabel={selectedDiff.label}
            sectionId={selectedDiff.sectionId}
            before={selectedDiff.before}
            after={selectedDiff.after}
            rationale={proposal?.rationale ?? ""}
            runtimeLabel={runtimeImpactLabel(manifest, selectedDiff.sectionId)}
            applying={applyPending}
            retrying={proposalPending}
            onApply={onApplyProposal}
            onAskAgain={onAskAgainProposal}
            onReject={onRejectProposal}
            onCancel={onCancelProposal}
          />
        ) : isEditing && selectedSection ? (
          <SectionActionPanel
            manifest={manifest}
            selectedSection={selectedSection}
            error={proposalError}
            pending={proposalPending}
            onRequestProposal={onRequestProposal}
          />
        ) : patchableSections.length > 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/90 px-5 py-5 md:px-7">
            <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Read mode
            </p>
            <h3 className="mt-2 text-base font-semibold text-slate-950">Editing stays off until you ask for it</h3>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Read the saved document first. When you are ready to revise it, enter edit mode to
              choose a section and generate a scoped proposal.
            </p>
          </div>
        ) : null}
      </article>
    </section>
  );
}

function ArtifactDocumentSection({
  section,
  changed,
  selected,
  diff,
  rationale,
  onSelect,
  editingEnabled,
}: {
  section: AgentWorkSurfaceSection;
  changed: boolean;
  selected: boolean;
  diff: {
    sectionId: string;
    label: string;
    before: AgentWorkbenchSectionContent;
    after: AgentWorkbenchSectionContent;
  } | null;
  rationale: string;
  onSelect?: (sectionId: string) => void;
  editingEnabled: boolean;
}) {
  const interactive = Boolean(onSelect);
  return (
    <section
      className={`rounded-xl border px-4 py-4 ${
        changed
          ? "border-brand-200 bg-brand-50/40"
          : selected
          ? "border-slate-300 bg-slate-50"
          : "border-transparent bg-white"
      }`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-data text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500">
            {section.content_kind}
          </p>
          <h3 className="mt-1 text-base font-semibold text-slate-950">{section.label}</h3>
        </div>
        <div className="flex items-center gap-2">
          {changed ? (
            <span className="rounded-full border border-brand-200 bg-white px-2.5 py-1 text-xs font-semibold text-brand-700">
              Changed
            </span>
          ) : null}
          {interactive ? (
            <Button variant={selected ? "primary" : "secondary"} size="sm" onClick={() => onSelect?.(section.id)}>
              {selected ? "Editing" : "Edit section"}
            </Button>
          ) : section.patchable && !editingEnabled ? (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
              Editable in edit mode
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
              Read only
            </span>
          )}
        </div>
      </div>
      <div className="text-sm leading-6 text-slate-800">{renderContent(section.content)}</div>
      {changed && diff ? (
        <div className="mt-4 space-y-3 rounded-xl border border-brand-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-brand-700">
              Proposed revision inline
            </p>
            <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
              Review this change here
            </span>
          </div>
          {rationale ? <p className="text-sm text-slate-600">{rationale}</p> : null}
          <div className="grid gap-3 xl:grid-cols-2">
            <DiffBlock label="Current document" content={diff.before} />
            <DiffBlock label="Proposed markdown" content={diff.after} highlighted />
          </div>
        </div>
      ) : null}
      {section.summary ? <p className="mt-3 text-xs text-slate-500">{section.summary}</p> : null}
    </section>
  );
}

function SectionActionPanel({
  manifest,
  selectedSection,
  error,
  pending,
  onRequestProposal,
}: {
  manifest: AgentWorkSurfaceManifest;
  selectedSection: AgentWorkSurfaceSection;
  error?: string | null;
  pending?: boolean;
  onRequestProposal?: ArtifactDocumentReviewProps["onRequestProposal"];
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
          Edit this section
        </p>
        <h3 className="mt-1 text-base font-semibold text-slate-950">{selectedSection.label}</h3>
        <p className="mt-2 text-sm text-slate-600">
          Start a scoped revision only when you want to change the saved document.
        </p>
      </div>
      <WorkbenchComposer
        actions={manifest.allowed_actions}
        selectedSectionId={selectedSection.id}
        selectedSectionLabel={selectedSection.label}
        error={error}
        pending={pending}
        onRequest={onRequestProposal}
      />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
          Runtime impact
        </p>
        <p className="mt-2 text-sm text-slate-700">{runtimeImpactLabel(manifest, selectedSection.id)}</p>
      </div>
    </div>
  );
}

function RevisionPanel({
  manifest,
  sectionLabel,
  sectionId,
  before,
  after,
  rationale,
  runtimeLabel,
  applying,
  retrying,
  onApply,
  onAskAgain,
  onReject,
  onCancel,
}: {
  manifest: AgentWorkSurfaceManifest;
  sectionLabel: string;
  sectionId: string;
  before: AgentWorkbenchSectionContent;
  after: AgentWorkbenchSectionContent;
  rationale: string;
  runtimeLabel: string;
  applying?: boolean;
  retrying?: boolean;
  onApply?: () => void;
  onAskAgain?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
}) {
  const runtimePreview = runtimeProjectionPreviewForSection(manifest, sectionId, after);
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-brand-200 bg-white p-4">
        <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-brand-700">
          Selected change
        </p>
        <h3 className="mt-1 text-base font-semibold text-slate-950">{sectionLabel}</h3>
        <p className="mt-2 text-sm text-slate-600">{rationale}</p>
      </div>
      <DiffBlock label="Before" content={before} />
      <DiffBlock label="After" content={after} highlighted />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
          Derived runtime preview
        </p>
        <p className="mt-2 text-sm text-slate-700">{runtimeLabel}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100">
          {JSON.stringify(runtimePreview, null, 2)}
        </pre>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={applying || retrying}>Cancel</Button>
        <Button variant="secondary" onClick={onReject} disabled={applying || retrying}>Reject</Button>
        <Button variant="secondary" onClick={onAskAgain} disabled={applying || retrying} loading={retrying}>
          Ask again
        </Button>
        <Button variant="primary" onClick={onApply} disabled={applying || retrying}>
          {applying ? "Applying..." : "Apply markdown revision"}
        </Button>
      </div>
    </div>
  );
}

function DiffBlock({
  label,
  content,
  highlighted = false,
}: {
  label: string;
  content: AgentWorkbenchSectionContent;
  highlighted?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-4 ${highlighted ? "border-brand-200 bg-brand-50" : "border-slate-200 bg-white"}`}>
      <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <div className="mt-2 text-sm leading-6 text-slate-800">{renderContent(content)}</div>
    </div>
  );
}

function runtimeImpactLabel(manifest: AgentWorkSurfaceManifest, sectionId: string): string {
  if (manifest.object_type === "brand_voice") {
    return `Updates the Brand Voice runtime projection for "${sectionId}", which is used when agent prompts assemble brand voice guidance.`;
  }
  if (manifest.object_type === "strategy_artifact") {
    return `Updates the Brand Strategy runtime projection for "${sectionId}", which is used when agent prompts assemble positioning context.`;
  }
  return `Updates the runtime projection for "${sectionId}" when this revision is applied.`;
}

function runtimeProjectionPreviewForSection(
  manifest: AgentWorkSurfaceManifest,
  sectionId: string,
  content: AgentWorkbenchSectionContent,
): Record<string, unknown> {
  return {
    object_type: manifest.object_type,
    section_id: sectionId,
    runtime_field: sectionId,
    projected_value: contentValueForPreview(content),
  };
}

function contentValueForPreview(content: AgentWorkbenchSectionContent): unknown {
  switch (content.kind) {
    case "text":
      return content.text;
    case "markdown":
      return content.markdown;
    case "string_list":
      return content.items;
    case "labeled_fields":
      return Object.fromEntries(content.fields.map((field) => [field.key, field.value]));
    case "json":
      return content.value;
    default:
      return assertNever(content);
  }
}

function renderContent(content: AgentWorkbenchSectionContent) {
  switch (content.kind) {
    case "text":
      return <p className="whitespace-pre-wrap">{content.text || "Not set yet."}</p>;
    case "markdown":
      return <div className="whitespace-pre-wrap">{content.markdown || "Not set yet."}</div>;
    case "string_list":
      return content.items.length ? (
        <ul className="space-y-2">
          {content.items.map((item, index) => (
            <li key={`${item}-${index}`} className="rounded-lg bg-white/70 px-3 py-2 ring-1 ring-slate-200">
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-slate-500">No items yet.</p>
      );
    case "labeled_fields":
      return (
        <dl className="grid gap-3 sm:grid-cols-2">
          {content.fields.map((field) => (
            <div key={field.key} className="rounded-lg bg-white/70 px-3 py-2 ring-1 ring-slate-200">
              <dt className="font-data text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500">
                {field.label}
              </dt>
              <dd className="mt-1">{field.value}</dd>
            </div>
          ))}
        </dl>
      );
    case "json":
      return (
        <pre className="overflow-x-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100">
          {JSON.stringify(content.value, null, 2)}
        </pre>
      );
    default:
      return assertNever(content);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled workbench content kind: ${JSON.stringify(value)}`);
}
